/**
 * @fileoverview Employee entitlement totals and FEG formula engine
 *
 * Purpose:
 * - Single source of truth for summing an employee's outstanding
 *   entitlements (previously duplicated across nine call sites).
 * - The nine formula-driven fields required by the Fair Entitlements
 *   Guarantee (FEG) Branch DB import, plus the enum taxonomies FEG
 *   expects.
 *
 * Lives in `convex/lib/` so it can be imported from both the Convex
 * backend (relative paths) and the React frontend (via the `@cvx`
 * alias) without duplicating logic.
 *
 * Two totals exist and they are NOT interchangeable:
 * - `sumEmployeeEntitlements` — Morgan's total, used by the estimated
 *   outcome waterfall, contacts CRM, precedent field mapping and the
 *   flagged-transaction registers. Includes unpaid superannuation
 *   because it is still a debt of the company for RATA purposes.
 * - `sumFegRecoverable` — what FEG will actually advance. Excludes
 *   superannuation (recovered via the SGC/ATO, not FEG).
 *
 * Neither total includes the per-category winding-up costs: those are
 * administration costs, not the employee's entitlement.
 */

// ---------------------------------------------------------------------------
// Taxonomies
// ---------------------------------------------------------------------------

/**
 * Basis of employment.
 *
 * The FEG Branch DB import accepts ONLY the first three values — its
 * instruction sheet reads "Choose from Full-time (permanent), Part-time
 * (permanent), Casual". The remaining three are Morgan-only: they exist
 * because the schema was widened before the real template was sighted,
 * and rows already carrying them must keep validating and displaying.
 *
 * Use `FEG_EMPLOYMENT_BASIS` for anything the FEG import will read, and
 * `isFegEmploymentBasis` to detect a value FEG cannot accept.
 */
export const EMPLOYMENT_BASIS_LABELS = {
  FT: "Full-time (permanent)",
  PT: "Part-time (permanent)",
  Casual: "Casual",
  Shiftwork: "Shiftwork",
  Probation: "Probation",
  Other: "Other",
} as const;

export type EmploymentBasis = keyof typeof EMPLOYMENT_BASIS_LABELS;

/** The only three values the FEG import will accept, in FEG's order. */
export const FEG_EMPLOYMENT_BASIS = ["FT", "PT", "Casual"] as const;

export type FegEmploymentBasis = (typeof FEG_EMPLOYMENT_BASIS)[number];

/** True when the basis is one FEG accepts. */
export function isFegEmploymentBasis(
  value: string | undefined | null,
): value is FegEmploymentBasis {
  return value === "FT" || value === "PT" || value === "Casual";
}

/**
 * FEG's "Employment Type" — distinct from Morgan's free-text `role`
 * (a job title such as "Labourer") and from the basis of employment.
 *
 * Labels are verbatim from the FEG instruction sheet, which the import
 * matches on. Do not reword them.
 */
export const EMPLOYMENT_TYPE_LABELS = {
  employee: "Employee",
  apprentice: "Apprentice",
  trainee: "Trainee",
  contractor: "(Sub)Contractor",
  tcf_outworker: "TCF contract outworker",
} as const;

export type EmploymentType = keyof typeof EMPLOYMENT_TYPE_LABELS;

/** State or territory in which the employee was employed. */
export const EMPLOYMENT_STATES = [
  "ACT",
  "NSW",
  "NT",
  "QLD",
  "SA",
  "TAS",
  "VIC",
  "WA",
] as const;

export type EmploymentState = (typeof EMPLOYMENT_STATES)[number];

/**
 * Relationship of the employee to a director or owner of the company.
 *
 * Labels are verbatim from the FEG instruction sheet, which the import
 * matches on. Do not reword them.
 */
export const DIRECTOR_RELATIONSHIP_LABELS = {
  none: "None",
  parent: "Parent",
  grandparent: "Grandparent",
  sibling: "Sibling",
  child: "Child",
  grandchild: "Grandchild",
  spouse: "Spouse (including de-facto)",
  other: "Other",
} as const;

export type DirectorRelationship = keyof typeof DIRECTOR_RELATIONSHIP_LABELS;

/** How the employment ended. */
export const TERMINATION_TYPE_LABELS = {
  notification: "Notification of termination",
  no_notification: "No notification of termination",
  resignation: "Resignation",
} as const;

export type TerminationType = keyof typeof TERMINATION_TYPE_LABELS;

/** Residency status (pre-existing taxonomy, surfaced here for label reuse). */
export const RESIDENCY_LABELS = {
  citizen: "Australian citizen",
  permanent_resident: "Permanent resident",
  temporary_visa: "Temporary visa",
  other: "Other",
} as const;

// ---------------------------------------------------------------------------
// Entitlement totals
// ---------------------------------------------------------------------------

/**
 * The stored dollar figures for one employee. Every field is optional —
 * an absent field means "not known / not applicable", which sums as 0.
 */
export interface EmployeeEntitlementAmounts {
  unpaidWages?: number;
  unpaidSuper?: number;
  accruedAnnualLeave?: number;
  /**
   * Annual leave loading. FEG treats this as its own category with its
   * own comments and winding-up cost, so it is stored separately from
   * `accruedAnnualLeave` rather than folded into it.
   */
  leaveLoading?: number;
  accruedLongServiceLeave?: number;
  paymentInLieuOfNotice?: number;
  redundancyPay?: number;
}

/** Components that make up Morgan's total employee debt. */
const MORGAN_TOTAL_KEYS = [
  "unpaidWages",
  "unpaidSuper",
  "accruedAnnualLeave",
  "leaveLoading",
  "accruedLongServiceLeave",
  "paymentInLieuOfNotice",
  "redundancyPay",
] as const satisfies ReadonlyArray<keyof EmployeeEntitlementAmounts>;

/**
 * Components FEG will advance. Superannuation is deliberately absent —
 * it is recovered through the superannuation guarantee charge via the
 * ATO, and the FEG template has no column for it.
 */
const FEG_RECOVERABLE_KEYS = [
  "unpaidWages",
  "accruedAnnualLeave",
  "leaveLoading",
  "accruedLongServiceLeave",
  "paymentInLieuOfNotice",
  "redundancyPay",
] as const satisfies ReadonlyArray<keyof EmployeeEntitlementAmounts>;

function sumKeys(
  entitlements: EmployeeEntitlementAmounts | undefined | null,
  keys: ReadonlyArray<keyof EmployeeEntitlementAmounts>,
): number {
  if (!entitlements) return 0;
  return keys.reduce((total, key) => {
    const value = entitlements[key];
    return typeof value === "number" && Number.isFinite(value)
      ? total + value
      : total;
  }, 0);
}

/**
 * Morgan's total for one employee — every entitlement component
 * including unpaid superannuation, excluding winding-up costs.
 *
 * This is the figure the estimated outcome waterfall, contacts CRM,
 * precedent field mapping and the flagged-transaction registers use.
 */
export function sumEmployeeEntitlements(
  entitlements: EmployeeEntitlementAmounts | undefined | null,
): number {
  return sumKeys(entitlements, MORGAN_TOTAL_KEYS);
}

/**
 * The FEG-recoverable total for one employee — excludes unpaid
 * superannuation and winding-up costs.
 */
export function sumFegRecoverable(
  entitlements: EmployeeEntitlementAmounts | undefined | null,
): number {
  return sumKeys(entitlements, FEG_RECOVERABLE_KEYS);
}

/**
 * Payments already made to the employee, or to someone on their behalf,
 * per entitlement category — the balance of a redundancy fund, a partial
 * dividend. FEG deducts these from the advance it pays.
 */
export interface EmployeePatAmounts {
  unpaidWages?: number;
  annualLeave?: number;
  longServiceLeave?: number;
  piln?: number;
  redundancy?: number;
}

const PAT_KEYS = [
  "unpaidWages",
  "annualLeave",
  "longServiceLeave",
  "piln",
  "redundancy",
] as const satisfies ReadonlyArray<keyof EmployeePatAmounts>;

/** Total already paid to the employee across all categories. */
export function sumPaymentsAfterTermination(
  pat: EmployeePatAmounts | undefined | null,
): number {
  if (!pat) return 0;
  return PAT_KEYS.reduce((total, key) => {
    const value = pat[key];
    return typeof value === "number" && Number.isFinite(value)
      ? total + value
      : total;
  }, 0);
}

/**
 * What FEG would still advance after deducting payments already made.
 *
 * Floored at zero: an overpayment in one category does not create a
 * negative claim.
 */
export function netFegRecoverable(
  entitlements: EmployeeEntitlementAmounts | undefined | null,
  pat: EmployeePatAmounts | undefined | null,
): number {
  return Math.max(
    0,
    sumFegRecoverable(entitlements) - sumPaymentsAfterTermination(pat),
  );
}

// ---------------------------------------------------------------------------
// Rounding
// ---------------------------------------------------------------------------

/**
 * Excel's `ROUNDDOWN(n, 2)` — truncates toward zero rather than toward
 * negative infinity, so `-1.239` becomes `-1.23`, not `-1.24`.
 *
 * The epsilon nudge absorbs binary floating-point representation error
 * so a value that is mathematically 1.15 does not truncate to 1.14.
 */
export function roundDownTo2(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const scaled = value * 100;
  const nudged =
    scaled >= 0 ? scaled + Number.EPSILON * 100 : scaled - Number.EPSILON * 100;
  return Math.trunc(nudged) / 100;
}

/**
 * Half-up rounding to 2 dp. The FEG import requires every monetary
 * value as a static number to two decimal places, so export rounds
 * even the formulas the template itself leaves unrounded.
 */
export function roundCurrency(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Parses an ISO `YYYY-MM-DD` date as UTC midnight. Returns null if unusable. */
function parseIsoDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Formats a stored ISO date as `DD/MM/YYYY` with leading zeros, the
 * format the FEG instruction sheet documents ("eg. 25/12/2002 or
 * 01/11/2002 (with leading zeros)").
 *
 * Emitted as a string rather than a real Excel date deliberately: an
 * Excel date cell carries a locale-dependent display format, and a
 * day/month transposition on an import we cannot re-check is a much
 * worse failure than a text cell. Returns an empty string when the date
 * is missing or unparseable, so the cell is simply left blank.
 */
export function formatFegDate(value: string | undefined | null): string {
  const parsed = parseIsoDate(value);
  if (!parsed) return "";
  const day = String(parsed.getUTCDate()).padStart(2, "0");
  const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${parsed.getUTCFullYear()}`;
}

/**
 * Strips spaces, hyphens and any other separator from an ABN or ACN.
 * The FEG sheet requires the ABN "without spaces or hyphens".
 */
export function digitsOnly(value: string | undefined | null): string {
  return (value ?? "").replace(/\D/g, "");
}

/**
 * Years of service between two dates, to two decimal places.
 *
 * Uses 365.25 days per year (the FEG template's leap-year allowance)
 * and rounds down, matching `ROUNDDOWN((to - from) / 365.25, 2)`.
 *
 * Returns `undefined` when either date is missing or the range is
 * inverted, so the caller can render an em dash rather than a
 * misleading zero.
 */
export function serviceYears(
  from: string | undefined | null,
  to: string | undefined | null,
): number | undefined {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  if (!start || !end) return undefined;
  const days = (end.getTime() - start.getTime()) / MS_PER_DAY;
  if (days < 0) return undefined;
  return roundDownTo2(days / 365.25);
}

/** The case-level dates the service formulas depend on. */
export interface FegCaseDates {
  administrationDate?: string;
  liquidationDate?: string;
  administrationContinuous?: boolean;
  /** Fallback when neither FEG-specific date has been recorded yet. */
  appointmentDate?: string;
}

/**
 * The insolvency practitioner appointment date FEG measures service to.
 *
 * Where the administration ran continuously into the liquidation, the
 * administration date is the relevant appointment date; otherwise the
 * liquidation date is. Falls back to the job's generic appointment date
 * so the calculation still works before the FEG-specific dates are
 * captured.
 */
export function resolveFegInsolvencyDate(
  caseDates: FegCaseDates | undefined | null,
): string | undefined {
  if (!caseDates) return undefined;
  const preferred = caseDates.administrationContinuous
    ? caseDates.administrationDate
    : caseDates.liquidationDate;
  return (
    preferred ??
    caseDates.liquidationDate ??
    caseDates.administrationDate ??
    caseDates.appointmentDate
  );
}

// ---------------------------------------------------------------------------
// Wage formulas
// ---------------------------------------------------------------------------

/** Weekly wage before allowances: hourly rate x average weekly hours. */
export function baseWeeklyWage(
  wageRate: number | undefined | null,
  averageWeeklyHours: number | undefined | null,
): number | undefined {
  if (typeof wageRate !== "number" || typeof averageWeeklyHours !== "number") {
    return undefined;
  }
  return wageRate * averageWeeklyHours;
}

/** Weekly wage including regular allowances (allowances absent means zero). */
export function totalWeeklyWage(
  base: number | undefined,
  regularWeeklyAllowances: number | undefined | null,
): number | undefined {
  if (base === undefined) return undefined;
  return base + (regularWeeklyAllowances ?? 0);
}

// ---------------------------------------------------------------------------
// Entitlement formulas
// ---------------------------------------------------------------------------

function multiply(
  a: number | undefined | null,
  b: number | undefined | null,
): number | undefined {
  if (typeof a !== "number" || typeof b !== "number") return undefined;
  return a * b;
}

/**
 * Annual leave owed: hourly rate x accrued hours at termination.
 * Left unrounded — leave loading is a separate category.
 */
export function annualLeaveOwed(
  wageRate: number | undefined | null,
  hoursAtTermination: number | undefined | null,
): number | undefined {
  return multiply(wageRate, hoursAtTermination);
}

/**
 * Leave loading owed on accrued annual leave hours, rounded down to
 * two decimal places.
 *
 * `ratePercent` is a percentage (17.5 means 17.5%), not a fraction —
 * the division by 100 happens here so the stored value is unambiguous.
 */
export function leaveLoadingTotal(
  hoursAtTermination: number | undefined | null,
  ratePercent: number | undefined | null,
  wageRate: number | undefined | null,
): number | undefined {
  if (
    typeof hoursAtTermination !== "number" ||
    typeof ratePercent !== "number" ||
    typeof wageRate !== "number"
  ) {
    return undefined;
  }
  return roundDownTo2(hoursAtTermination * (ratePercent / 100) * wageRate);
}

/** Long service leave owed: hourly rate x accrued hours. Unrounded. */
export function longServiceLeaveOwed(
  wageRate: number | undefined | null,
  hoursAtTermination: number | undefined | null,
): number | undefined {
  return multiply(wageRate, hoursAtTermination);
}

/**
 * Payment in lieu of notice: total weekly wage x weeks owing.
 *
 * PILN uses the TOTAL weekly wage (allowances included). This differs
 * from redundancy deliberately — it is not an oversight in the FEG model.
 */
export function pilnOwed(
  total: number | undefined,
  weeksOwing: number | undefined | null,
): number | undefined {
  return multiply(total, weeksOwing);
}

/**
 * Redundancy: base weekly wage x weeks owing.
 *
 * Redundancy uses the BASE weekly wage (allowances excluded), unlike
 * PILN which uses the total.
 */
export function redundancyOwed(
  base: number | undefined,
  weeksOwing: number | undefined | null,
): number | undefined {
  return multiply(base, weeksOwing);
}

// ---------------------------------------------------------------------------
// Combined derivation
// ---------------------------------------------------------------------------

/**
 * A stored figure paired with what the FEG formula produces from the
 * captured inputs, so the UI can show the variance and offer to adopt
 * the calculated value.
 */
export interface DerivedAmount {
  /** What is currently persisted on the entry. */
  stored?: number;
  /** What the FEG formula produces, or undefined if inputs are missing. */
  calculated?: number;
  /** `calculated - stored`, only set when both sides are known. */
  variance?: number;
  /** True when both sides are known and differ by more than a cent. */
  hasVariance: boolean;
}

function toDerived(
  stored: number | undefined,
  calculated: number | undefined,
): DerivedAmount {
  if (stored === undefined || calculated === undefined) {
    return { stored, calculated, hasVariance: false };
  }
  const variance = calculated - stored;
  return {
    stored,
    calculated,
    variance,
    hasVariance: Math.abs(variance) >= 0.01,
  };
}

/** The subset of an employee entry the FEG formulas read. */
export interface FegCalcInput {
  startDate?: string;
  terminationDate?: string;
  wageRate?: number;
  averageWeeklyHours?: number;
  regularWeeklyAllowances?: number;
  entitlements?: EmployeeEntitlementAmounts;
  annualLeaveDetail?: { hoursAtTermination?: number };
  leaveLoadingDetail?: { ratePercent?: number };
  longServiceLeaveDetail?: { hoursAtTermination?: number };
  pilnDetail?: { weeksOwing?: number };
  redundancyDetail?: { weeksOwing?: number };
}

/** Everything the FEG template derives rather than captures. */
export interface FegDerived {
  /** Years of service from commencement to the IP appointment date. */
  serviceAtAppointment?: number;
  /** Years of service from commencement to termination. */
  serviceAtTermination?: number;
  /** The appointment date the service calculation resolved to. */
  insolvencyDate?: string;
  baseWeeklyWage?: number;
  totalWeeklyWage?: number;
  annualLeave: DerivedAmount;
  leaveLoading: DerivedAmount;
  longServiceLeave: DerivedAmount;
  piln: DerivedAmount;
  redundancy: DerivedAmount;
}

/**
 * Computes every FEG-derived value for one employee.
 *
 * Evaluated in dependency order: hourly rate and average hours give the
 * base weekly wage, allowances lift that to the total weekly wage, and
 * redundancy then keys off the base while PILN keys off the total.
 */
export function computeFegDerived(
  entry: FegCalcInput,
  caseDates: FegCaseDates | undefined | null,
): FegDerived {
  const insolvencyDate = resolveFegInsolvencyDate(caseDates);

  const base = baseWeeklyWage(entry.wageRate, entry.averageWeeklyHours);
  const total = totalWeeklyWage(base, entry.regularWeeklyAllowances);

  const ent = entry.entitlements ?? {};

  return {
    serviceAtAppointment: serviceYears(entry.startDate, insolvencyDate),
    serviceAtTermination: serviceYears(entry.startDate, entry.terminationDate),
    insolvencyDate,
    baseWeeklyWage: base,
    totalWeeklyWage: total,
    annualLeave: toDerived(
      ent.accruedAnnualLeave,
      annualLeaveOwed(
        entry.wageRate,
        entry.annualLeaveDetail?.hoursAtTermination,
      ),
    ),
    leaveLoading: toDerived(
      ent.leaveLoading,
      leaveLoadingTotal(
        entry.annualLeaveDetail?.hoursAtTermination,
        entry.leaveLoadingDetail?.ratePercent,
        entry.wageRate,
      ),
    ),
    longServiceLeave: toDerived(
      ent.accruedLongServiceLeave,
      longServiceLeaveOwed(
        entry.wageRate,
        entry.longServiceLeaveDetail?.hoursAtTermination,
      ),
    ),
    piln: toDerived(
      ent.paymentInLieuOfNotice,
      pilnOwed(total, entry.pilnDetail?.weeksOwing),
    ),
    redundancy: toDerived(
      ent.redundancyPay,
      redundancyOwed(base, entry.redundancyDetail?.weeksOwing),
    ),
  };
}

// ---------------------------------------------------------------------------
// Name splitting
// ---------------------------------------------------------------------------

/**
 * Best-effort split of a single display name into family name and
 * first name for the FEG import, which requires them as separate
 * columns.
 *
 * Handles the two shapes payroll exports actually produce: "SMITH, John"
 * (comma-delimited, family name first) and "John Smith" (given names
 * first). Everything after the first token of a space-delimited name is
 * treated as the family name so "John Van Der Berg" keeps its
 * multi-word surname intact.
 *
 * Only ever a fallback — a practitioner-corrected `familyName` /
 * `firstName` on the entry always wins.
 */
export function splitEmployeeName(name: string | undefined | null): {
  familyName: string;
  firstName: string;
} {
  const trimmed = (name ?? "").trim().replace(/\s+/g, " ");
  if (!trimmed) return { familyName: "", firstName: "" };

  const commaIndex = trimmed.indexOf(",");
  if (commaIndex > 0) {
    return {
      familyName: trimmed.slice(0, commaIndex).trim(),
      firstName: trimmed.slice(commaIndex + 1).trim(),
    };
  }

  const parts = trimmed.split(" ");
  if (parts.length === 1) return { familyName: parts[0], firstName: "" };
  return {
    firstName: parts[0],
    familyName: parts.slice(1).join(" "),
  };
}

/**
 * Recombines the structured name parts into the single `name` field
 * that remains Morgan's dedup key. Returns undefined when there is
 * nothing to build from, so callers leave `name` untouched.
 */
export function joinEmployeeName(
  firstName: string | undefined | null,
  familyName: string | undefined | null,
): string | undefined {
  const joined = [firstName, familyName]
    .map((part) => (part ?? "").trim())
    .filter((part) => part.length > 0)
    .join(" ");
  return joined.length > 0 ? joined : undefined;
}
